import { useCallback, useState } from "react";
import { Timestamp, doc, runTransaction } from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import { type AssignmentHistoryEntry } from "@/hooks/useTenants";

export type AddMembersResult =
  { ok: true; addedCount: number; skippedEmails: string[] } | { ok: false };

export function useAddTenantMembers(tenantId: string) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addMembers = useCallback(
    async (emails: string[]): Promise<AddMembersResult> => {
      if (emails.length === 0) {
        return { ok: true, addedCount: 0, skippedEmails: [] };
      }
      setIsSubmitting(true);
      try {
        const tenantRef = doc(db, "tenants", tenantId);
        let addedCount = 0;
        let skippedEmails: string[] = [];

        await runTransaction(db, async (tx) => {
          const snap = await tx.get(tenantRef);
          if (!snap.exists()) {
            throw new Error(`Tenant ${tenantId} does not exist`);
          }

          const data = snap.data();
          const currentEmails: string[] = data.active_user_emails ?? [];
          const currentHistory: AssignmentHistoryEntry[] =
            data.assignment_history ?? [];

          const currentEmailSet = new Set(
            currentEmails.map((email) => email.toLowerCase()),
          );
          const newEmails: string[] = [];
          const skipped: string[] = [];
          for (const email of emails) {
            const normalized = email.toLowerCase();
            if (currentEmailSet.has(normalized)) {
              skipped.push(email);
            } else {
              newEmails.push(email);
              currentEmailSet.add(normalized);
            }
          }

          addedCount = newEmails.length;
          skippedEmails = skipped;

          if (newEmails.length === 0) return;

          const now = Timestamp.now();
          const newHistory: AssignmentHistoryEntry[] = newEmails.map(
            (email) => ({ action: "added", email, date: now }),
          );

          tx.update(tenantRef, {
            active_user_emails: [...currentEmails, ...newEmails],
            assignment_history: [...currentHistory, ...newHistory],
          });
        });

        return { ok: true, addedCount, skippedEmails };
      } catch (err) {
        logError(err as Error);
        return { ok: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [tenantId],
  );

  return { addMembers, isSubmitting };
}
